import { t } from "ttag";
import { formatFirmwareDate } from "../DeviceManagment/firmwareUiUtils";

export interface IGapRow {
  entityId?: string;
  entityName?: string;
  statType?: string;
  startTs?: string | Date;
  endTs?: string | Date;
  durationMinutes?: number;
  wasSplit?: boolean;
  samples?: number;
  firstSampleTs?: string | Date;
  lastSampleTs?: string | Date;
  residualMinutes?: number;
  status?: string;
  retried?: boolean;
  errorMessage?: string;
}

export interface ITelemetryRecoveryAudit {
  id: string;
  serial?: string;
  dmid?: string;
  connectionTs?: string | Date;
  startedTs?: string | Date;
  finishedTs?: string | Date;
  durationMs?: number;
  isCachingEnabled?: boolean;
  sdCardConnected?: boolean;
  sdCardSource?: "config" | "probe" | null;
  retriedGaps?: number;
  recoveredOnRetry?: number;
  outcome?: string;
  skipReason?: string | null;
  statsChecked?: string[];
  totals?: { [status: string]: number };
  hasGaps?: boolean;
  hasNoData?: boolean;
  hasNoResponse?: boolean;
  HaveIssues?: boolean;
  fullyRecovered?: boolean;
  gapsTruncated?: boolean;
  gaps?: IGapRow[];
}

export interface IGapGroup {
  statType: string;
  rows: IGapRow[];
}

export type ChipTone = "error" | "warning" | "success" | "default";

export interface IStatusChip {
  label: string;
  tone: ChipTone;
}

/** Same `YYYY-MM-DD HH:mm:ss` / "-" formatter the firmware screens use. */
export const formatTs = formatFirmwareDate;

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const asNumber = typeof value === "number" ? value : Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
}

function count(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * "7 days, 4 hours, 20 minutes". The input is rounded to whole seconds first;
 * seconds only appear when the total is under a minute; "-" for anything that is
 * not a non-negative finite number.
 */
export function formatDurationMs(ms: unknown): string {
  const value = toFiniteNumber(ms);
  if (value === null || value < 0) {
    return "-";
  }
  const totalSeconds = Math.round(value / MS_PER_SECOND);
  if (totalSeconds < SECONDS_PER_MINUTE) {
    return count(totalSeconds, t`second`, t`seconds`);
  }
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const parts: string[] = [];
  if (days) {
    parts.push(count(days, t`day`, t`days`));
  }
  if (hours) {
    parts.push(count(hours, t`hour`, t`hours`));
  }
  if (minutes) {
    parts.push(count(minutes, t`minute`, t`minutes`));
  }
  return parts.join(", ");
}

/** Gap rows carry minutes (float), not milliseconds. */
export function formatDurationMinutes(minutes: unknown): string {
  const value = toFiniteNumber(minutes);
  if (value === null) {
    return "-";
  }
  return formatDurationMs(value * MS_PER_MINUTE);
}

/** "850 ms" / "1.2 s" — for the handshake time, which is short. */
export function formatMillisShort(ms: unknown): string {
  const value = toFiniteNumber(ms);
  if (value === null || value < 0) {
    return "-";
  }
  if (value < MS_PER_SECOND) {
    return `${Math.round(value)} ms`;
  }
  return `${(value / MS_PER_SECOND).toFixed(1)} s`;
}

/** Elapsed time of a session that is still open, computed at render. */
export function formatElapsedSince(startTs: unknown, now: number = Date.now()): string {
  if (!startTs) {
    return "-";
  }
  const started = new Date(startTs as any).getTime();
  return formatDurationMs(now - started);
}

export function yesNo(value: unknown): string {
  if (value === true) {
    return t`Yes`;
  }
  if (value === false) {
    return t`No`;
  }
  return "-";
}

export function gapStatusLabel(status: string | undefined): string {
  switch (status) {
    case "no_data":
      return t`No data`;
    case "no_response":
      return t`No response`;
    case "partial_data":
      return t`Partial data`;
    case "full_data":
      return t`Full data`;
    default:
      return status || "-";
  }
}

export function gapStatusTone(status: string | undefined): ChipTone {
  switch (status) {
    case "no_data":
    case "no_response":
      return "error";
    case "partial_data":
      return "warning";
    case "full_data":
      return "success";
    default:
      return "default";
  }
}

export function skipReasonLabel(reason: string | null | undefined): string {
  switch (reason) {
    case "cachingDisabled":
      return t`Caching disabled`;
    case "sdCardNotConnected":
      return t`SD card not connected`;
    case "probeError":
      return t`SD-card probe error`;
    case "probeFailed":
      return t`SD-card probe failed`;
    case "noGaps":
      return t`No gaps`;
    default:
      return reason || "-";
  }
}

export function statTypeLabel(statType: string | undefined): string {
  switch (statType) {
    case "istat":
      return t`Indoor units (istat)`;
    case "ostat":
      return t`Outdoor units (ostat)`;
    case "sstat":
      return t`Sensors (sstat)`;
    case "pstat":
      return t`Power meters (pstat)`;
    case "bstat":
      return t`BACnet units (bstat)`;
    case "mstat":
      return t`Modbus units (mstat)`;
    default:
      return statType || "-";
  }
}

/** `skipReason: 'noGaps'` is written on completed runs, so it folds into the outcome. */
export function outcomeLabel(audit: Partial<ITelemetryRecoveryAudit>): string {
  switch (audit.outcome) {
    case "completed":
      return audit.skipReason === "noGaps" ? t`Completed, no gaps` : t`Completed`;
    case "skipped":
      return t`Skipped`;
    case "abortedDisconnected":
      return t`Aborted (device disconnected)`;
    default:
      return audit.outcome || "-";
  }
}

/**
 * Exactly one chip per run, most specific first. The writer sets HaveIssues for any
 * non-full_data gap, for a probeFailed skip and for an aborted run, so Issues has to
 * come after the two more specific flags.
 */
export function statusChip(audit: Partial<ITelemetryRecoveryAudit>): IStatusChip {
  if (audit.hasNoResponse) {
    return { label: t`No response`, tone: "error" };
  }
  if (audit.hasNoData) {
    return { label: t`No data`, tone: "error" };
  }
  if (audit.HaveIssues) {
    return { label: t`Issues`, tone: "warning" };
  }
  if (audit.fullyRecovered) {
    return { label: t`Fully recovered`, tone: "success" };
  }
  if (audit.outcome === "completed") {
    return { label: t`No gaps`, tone: "default" };
  }
  if (audit.outcome === "skipped") {
    return { label: t`Skipped`, tone: "default" };
  }
  return { label: audit.outcome || "-", tone: "default" };
}

/** Shown instead of the gap tables when the run reached no stat type at all. */
export function skipBlockText(audit: Partial<ITelemetryRecoveryAudit>): string {
  switch (audit.skipReason) {
    case "cachingDisabled":
      return t`This run was skipped before any stat type was checked: caching is disabled on the device.`;
    case "sdCardNotConnected":
      return t`This run was skipped before any stat type was checked: the SD card is not connected.`;
    case "probeError":
      return t`This run was skipped before any stat type was checked: the SD-card probe errored.`;
    case "probeFailed":
      return t`This run was skipped before any stat type was checked: the SD-card probe failed.`;
    case "noGaps":
      return t`No gaps were found in this run.`;
    case null:
    case undefined:
    case "":
      return t`No stat type was reached in this run.`;
    default:
      return t`This run was skipped before any stat type was checked (${audit.skipReason}).`;
  }
}

function tsValue(value: string | Date | undefined): number {
  if (!value) {
    return 0;
  }
  const time = new Date(value as any).getTime();
  return Number.isFinite(time) ? time : 0;
}

/**
 * One group per stat type, in the order the run checked them, then any other stat
 * type that appears in the rows; rows newest first.
 */
export function groupGapsByStatType(
  gaps: IGapRow[] | undefined,
  statsChecked: string[] | undefined
): IGapGroup[] {
  const order: string[] = [];
  (statsChecked || []).forEach((statType) => {
    if (statType && !order.includes(statType)) {
      order.push(statType);
    }
  });
  (gaps || []).forEach((gap) => {
    const statType = gap?.statType || "";
    if (statType && !order.includes(statType)) {
      order.push(statType);
    }
  });
  return order.map((statType) => ({
    statType,
    rows: (gaps || [])
      .filter((gap) => gap?.statType === statType)
      .sort((a, b) => tsValue(b.startTs) - tsValue(a.startTs))
  }));
}
